import { z } from 'zod';
import type { DictateDownloadTarget } from '../../contracts/desktop-download';

/**
 * How long the Mac CPU check waits for UA Client Hints before it falls back to
 * Apple Silicon. Hints can be refused or simply never resolve.
 */
export const DICTATE_ARCHITECTURE_HINT_TIMEOUT_MS = 1_000;

const APPLE_SILICON: DictateDownloadTarget = {
  os: 'mac',
  installer: 'mac-apple-silicon',
};
const MAC_INTEL: DictateDownloadTarget = { os: 'mac', installer: 'mac-intel' };
const WINDOWS_EXE: DictateDownloadTarget = { os: 'windows', installer: 'windows-exe' };
const UNSUPPORTED: DictateDownloadTarget = { os: 'unsupported', installer: null };

const architectureHintsSchema = z.object({
  architecture: z.string().optional(),
  bitness: z.string().optional(),
});

/** `navigator.userAgentData` is Chromium-only and not in the TS DOM lib yet. */
type NavigatorWithUaData = Navigator & {
  userAgentData?: { getHighEntropyValues?: (hints: string[]) => Promise<unknown> };
};

/**
 * The Mac build from UA Client Hints. Only an explicit 64-bit x86 answer picks
 * Intel; a missing, refused, malformed, ambiguous or late answer picks Apple
 * Silicon (the product decision — every Mac sold since 2020). A Mac user agent
 * always says "Intel", so it is never evidence either way.
 */
const resolveMacInstaller = async (
  navigator: NavigatorWithUaData
): Promise<DictateDownloadTarget> => {
  const userAgentData = navigator.userAgentData;
  if (typeof userAgentData?.getHighEntropyValues !== 'function') return APPLE_SILICON;

  const readHints = async (): Promise<DictateDownloadTarget> => {
    const parsed = architectureHintsSchema.safeParse(
      await userAgentData.getHighEntropyValues?.(['architecture', 'bitness'])
    );
    if (!parsed.success || parsed.data.bitness !== '64') return APPLE_SILICON;
    return parsed.data.architecture === 'x86' ? MAC_INTEL : APPLE_SILICON;
  };

  let cancelTimeout = () => {};
  const timedOut = new Promise<DictateDownloadTarget>((resolve) => {
    const timeout = setTimeout(
      () => resolve(APPLE_SILICON),
      DICTATE_ARCHITECTURE_HINT_TIMEOUT_MS
    );
    cancelTimeout = () => clearTimeout(timeout);
  });
  try {
    // A late answer loses the race and is ignored.
    return await Promise.race([readHints().catch(() => APPLE_SILICON), timedOut]);
  } finally {
    cancelTimeout();
  }
};

/**
 * The Dictate installer for this browser. Browser-only: call after mount. Order
 * matters — iPadOS reports a Mac user agent, and Android reports Linux — so
 * touch devices are ruled out before any desktop match, and nothing
 * unrecognised ever falls through to Mac.
 */
export const detectDictateDownloadDevice = (): Promise<DictateDownloadTarget> => {
  if (typeof navigator === 'undefined') return Promise.resolve(UNSUPPORTED);
  const userAgent = navigator.userAgent ?? '';

  if (/iPad|iPhone|iPod|Android/.test(userAgent)) return Promise.resolve(UNSUPPORTED);
  if (/Macintosh/.test(userAgent) && navigator.maxTouchPoints > 1) {
    return Promise.resolve(UNSUPPORTED);
  }
  if (/Windows NT|Win64|Win32/.test(userAgent)) return Promise.resolve(WINDOWS_EXE);
  if (/Macintosh|Mac OS X/.test(userAgent)) return resolveMacInstaller(navigator);
  return Promise.resolve(UNSUPPORTED);
};
